import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { Loader2, Filter, MessageCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useWaMsg } from "@/lib/waTemplates";

const digits = (s) => (s || "").replace(/\D/g, "");

export default function OwnerFunnel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const shopName = user?.shop?.name || "nossa oficina";
  const waMsg = useWaMsg();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/crm/budgets");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>;
  if (!data) return null;

  const columns = [
    { key: "pending", label: "Enviado · aguardando", items: data.pending || [], tone: "border-amber-brand/40 bg-amber-brand/5", testid: "funnel-col-pending" },
    { key: "approved", label: "Aprovado", items: data.approved_items || [], tone: "border-green-brand/40 bg-green-brand/5", testid: "funnel-col-approved" },
    { key: "lost", label: "Recusado / perdido", items: data.lost_items || [], tone: "border-red-900 bg-red-950/20", testid: "funnel-col-lost" },
  ];

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><Filter className="h-4 w-4" /> Funil de orçamentos</div>
          <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Orçamentos</h1>
          <p className="text-sm text-zinc-400 mt-1">Taxa de aprovação no período: <span data-testid="approval-rate" className="font-bold text-green-neon">{Math.round((data.approval_rate || 0) * 100)}%</span></p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span data-testid="funnel-count-pending" className="rounded-full border border-amber-brand/40 bg-amber-brand/10 text-amber-200 px-2 py-0.5">Enviado: {data.pending?.length || 0}</span>
          <span data-testid="funnel-count-approved" className="rounded-full border border-green-brand/40 bg-green-brand/10 text-green-neon px-2 py-0.5">Aprovado: {data.approved_count}</span>
          <span data-testid="funnel-count-lost" className="rounded-full border border-red-900 bg-red-950/40 text-red-300 px-2 py-0.5">Recusado: {data.lost_count}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {columns.map((col) => (
          <div key={col.key} data-testid={col.testid} className={`rounded-2xl border ${col.tone} p-3`}>
            <div className="text-xs font-mono-caps uppercase mb-2 text-zinc-300">{col.label} · {col.items.length}</div>
            {col.items.length === 0 ? <p className="text-xs text-zinc-500">Nenhum.</p> : (
              <ul className="space-y-2">
                {col.items.map((it) => {
                  const d = digits(it.phone);
                  const wa = d ? (d.length >= 12 ? d : `55${d}`) : "";
                  const msg = encodeURIComponent(waMsg("budget_followup", { cliente: it.customer_name || "", placa: it.plate || "", oficina: shopName, valor: it.total != null ? brl(it.total) : "", link: `${window.location.origin}/cliente/ordens/${it.order_id}` }));
                  const href = wa ? `https://wa.me/${wa}?text=${msg}` : "";
                  return (
                    <li key={it.order_id} data-testid={`funnel-item-${it.order_id}`} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <Link to={`/owner/ordens/${it.order_id}`} className="text-sm font-bold text-zinc-100 hover:text-green-neon">
                          OS {it.order_number != null ? `#${it.order_number}` : ""} · <span className="font-mono text-xs text-zinc-400">{it.plate || "—"}</span>
                        </Link>
                        <span className="font-display text-sm font-black text-green-neon">{brl(it.total || 0)}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{it.customer_name || "—"}</div>
                      {col.key === "pending" && it.days_pending != null && (
                        <div className={`text-[11px] mt-1 flex items-center gap-1 ${it.stale ? "text-red-300 font-semibold" : "text-zinc-400"}`}>
                          <Clock className="h-3 w-3" /> {it.days_pending} dia(s) parado{it.stale ? " · cobrar!" : ""}
                        </div>
                      )}
                      {col.key === "pending" && href && it.stale && (
                        <a data-testid={`funnel-wa-${it.order_id}`} href={href} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 h-8 px-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase"><MessageCircle className="h-3.5 w-3.5" /> Cobrar</a>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
